import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressBarModule } from 'primeng/progressbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { OrderListModule } from 'primeng/orderlist';
import { DifficultyPipe } from '../../../pipes/difficulty.pipe';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TicketService } from '../../../core/services/ticket.service';
import { TicketDetail, TicketTask, TaskProgressResponse } from '../../../core/models/ticket.model';
import {
  findActiveTaskLog,
  getCurrentUserId,
  isTaskTimeEntryBlocked,
} from '../../../core/utils/active-task-log.util';
import { TcDataService } from '../../../../../shared/services/ticket-center/tc-data.service';
import { FavoriteSelectComponent } from '../../shared/favorite-input';
import { FavoriteListKey } from '../../../core/models/favorite-list.model';
import { FavoriteOptionsService } from '../../../core/services/favorite-options.service';
import { constants } from '../../../../../shared/utils/constants';

interface TaskWithProgress extends TicketTask {
  progress: number;
}

@Component({
  selector: 'app-task-management-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    DatePickerModule,
    ProgressBarModule,
    ConfirmDialogModule,
    TooltipModule,
    OrderListModule,
    DifficultyPipe,
    FavoriteSelectComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './task-management-modal.component.html',
  styleUrls: ['./task-management-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskManagementModalComponent implements OnInit {
  /** Enum das listas de favoritos, para uso no template. */
  protected readonly FavoriteKey = FavoriteListKey;

  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dataService = inject(TcDataService);
  private readonly favoriteOptions = inject(FavoriteOptionsService);

  @Input({ required: true }) set ticket(value: TicketDetail) {
    this._ticket = value;

    if (this._visible) {
      this.loadTasksWithProgress();
    }
  }

  get ticket(): TicketDetail {
    return this._ticket;
  }

  private _ticket!: TicketDetail;
  @Input() canEdit = true;

  @Input() set visible(value: boolean) {
    this._visible = value;
    if (value) {
      this.loadTasksWithProgress();
      this.closeForm();
    }
  }

  get visible(): boolean {
    return this._visible;
  }

  private _visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() tasksUpdated = new EventEmitter<TicketTask[]>();
  @Output() startTimeEntry = new EventEmitter<string>();
  @Output() ticketRefreshRequired = new EventEmitter<'status' | 'full'>();

  tasks = signal<TaskWithProgress[]>([]);
  showForm = signal(false);
  editingTask = signal<TicketTask | null>(null);
  saving = signal(false);
  draggedIndex = signal<number | null>(null);
  dropTarget = signal<{ index: number; position: 'top' | 'bottom' } | null>(null);

  formSummary = signal('');
  formDescription = signal('');
  formEstimatedEffort = signal<number | null>(null);
  formExpectedDate = signal<Date | null>(null);
  formDifficulty = signal('M');
  formStatusId = signal<string | null>(null);
  formUserId = signal<string | null>(null);
  formChangeExpectedDate = signal(true);
  formFinished = signal(false);

  statusesList = signal<any[]>([]);
  statusesLoading = signal(false);

  usersList = signal<any[]>([]);
  usersLoading = signal(false);

  readonly difficultyOptions = [
    { label: 'Fácil', value: 'F' },
    { label: 'Médio', value: 'M' },
    { label: 'Difícil', value: 'D' },
  ];

  totalPoints = computed(() => {
    return this.tasks().reduce((sum, t) => sum + (t.estimated_effort ?? 0), 0);
  });

  finishedPoints = computed(() => {
    return this.tasks()
      .filter(t => !!t.finished)
      .reduce((sum, t) => sum + (t.estimated_effort ?? 0), 0);
  });

  overallProgress = computed(() => {
    const total = this.totalPoints();
    if (total === 0) return 0;
    return Math.min(100, (this.finishedPoints() / total) * 100);
  });

  formTitle = computed(() => {
    return this.editingTask() ? 'Editar Tarefa' : 'Nova Tarefa';
  });

  ngOnInit(): void {
    this.loadTasksWithProgress();
  }

  private loadTasksWithProgress(): void {
    this.ticketService.listTasks(this.ticket.id).subscribe({
      next: (tasks: TicketTask[]) => {
        const sortedTasks = [...tasks].sort((a, b) => a.sequence - b.sequence);
        const tasksWithProgress: TaskWithProgress[] = sortedTasks.map(t => ({
          ...t,
          finished: !!t.finished,
          progress: 0,
        }));
        this.tasks.set(tasksWithProgress);
        this.cdr.markForCheck();

        this.ticketService.getTasksProgress(this.ticket.id).subscribe({
          next: progressData => {
            const updatedWithProgress = this.tasks().map((task, index) => ({
              ...task,
              progress: Array.isArray(progressData)
                ? (progressData[index]?.percentage ?? 0)
                : (progressData[task.id]?.percentage ?? 0),
            }));
            this.tasks.set(updatedWithProgress);
            this.cdr.markForCheck();
          },
          error: (err: unknown) => console.error('Error loading task progress:', err),
        });
      },
      error: (err: unknown) => {
        console.error('Error loading tasks:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as tarefas.',
        });
      },
    });
  }

  openNewTaskForm(): void {
    if (!this.canEdit) return;
    this.resetForm();
    this.editingTask.set(null);

    // Seed users with ticket developer or responsible
    if (this.ticket?.developer?.id) {
      this.formUserId.set(this.ticket.developer.id);
      this.usersList.set([this.ticket.developer]);
    } else if (this.ticket?.responsible?.id) {
      this.formUserId.set(this.ticket.responsible.id);
      this.usersList.set([this.ticket.responsible]);
    } else {
      this.usersList.set([]);
    }

    // Seed statuses: sem tarefas no chamado, a primeira nasce em "Esforço Definido";
    // a partir daí herda o status atual do chamado.
    const seedStatus =
      (this.tasks()?.length ?? 0) === 0
        ? constants.ticketcenter.defaultFirstTaskStatus
        : this.ticket?.status?.id
          ? this.ticket.status
          : null;

    if (seedStatus) {
      this.formStatusId.set(seedStatus.id);
      this.statusesList.set([seedStatus]);
    } else {
      this.statusesList.set([]);
    }

    this.loadInitialOptions();

    this.showForm.set(true);
  }

  openEditTaskForm(task: TicketTask): void {
    if (!this.canEdit) return;
    this.editingTask.set(task);
    this.formSummary.set(task.summary || '');
    this.formDescription.set(task.description || '');
    this.formEstimatedEffort.set(task.estimated_effort);
    this.formExpectedDate.set(task.expected_date ? new Date(task.expected_date) : null);
    this.formDifficulty.set(task.difficulty || 'M');
    this.formStatusId.set(task.status?.id || null);
    this.formUserId.set(task.user?.id || null);
    this.formFinished.set(!!task.finished);

    if (task.user) {
      this.usersList.set([task.user]);
    }

    if (task.status) {
      this.statusesList.set([task.status]);
    }

    this.loadInitialOptions();

    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.editingTask.set(null);
    this.resetForm();
  }

  private resetForm(): void {
    this.formSummary.set('');
    this.formDescription.set('');
    this.formEstimatedEffort.set(1);
    this.formExpectedDate.set(null);
    this.formDifficulty.set('M');
    this.formStatusId.set(null);
    this.formUserId.set(null);
    this.formChangeExpectedDate.set(true);
    this.formFinished.set(false);
  }

  /**
   * Buscas iniciais dos selects do formulário. São puladas quando a seleção vai
   * abrir só com os favoritos — quem resolve a listagem nesse caso é o próprio
   * componente de seleção.
   */
  private loadInitialOptions(): void {
    if (!this.favoriteOptions.hasFavorites(FavoriteListKey.TicketDevelopers)) {
      this.loadUsers();
    }
    if (!this.favoriteOptions.hasFavorites(FavoriteListKey.TicketStatuses)) {
      this.loadStatuses();
    }
  }

  loadStatuses(query: string = ''): void {
    this.statusesLoading.set(true);
    this.ticketService.searchTicketStatuses(query).subscribe({
      next: statuses => {
        const currentId = this.formStatusId();
        if (currentId && !statuses.some((s: any) => s.id === currentId)) {
          const currentStatus = this.statusesList().find(s => s.id === currentId);
          if (currentStatus) {
            statuses.unshift(currentStatus);
          }
        }
        this.statusesList.set(statuses);
        this.statusesLoading.set(false);
      },
      error: () => this.statusesLoading.set(false),
    });
  }

  loadUsers(query: string = ''): void {
    this.usersLoading.set(true);
    this.ticketService.searchUsers(query).subscribe({
      next: users => {
        const currentId = this.formUserId();
        if (currentId && !users.some((u: any) => u.id === currentId)) {
          const currentUser = this.usersList().find(u => u.id === currentId);
          if (currentUser) {
            users.unshift(currentUser);
          }
        }
        this.usersList.set(users);
        this.usersLoading.set(false);
      },
      error: () => this.usersLoading.set(false),
    });
  }

  onStatusFilter(event: any): void {
    this.loadStatuses(event.filter || '');
  }

  onUserFilter(event: any): void {
    this.loadUsers(event.filter || '');
  }

  saveTask(): void {
    const summary = this.formSummary().trim();
    const description = this.formDescription().trim();
    const statusId = this.formStatusId();

    const userId = this.formUserId();

    const effort = this.formEstimatedEffort();

    if (!summary || !statusId || !userId || !effort) {
      const missing: string[] = [];
      if (!summary) missing.push('descrição resumida');
      if (!statusId) missing.push('status');
      if (!userId) missing.push('desenvolvedor');
      if (!effort) missing.push('esforço técnico');
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `Campo(s) obrigatório(s): ${missing.join(', ')}`,
        life: 3000,
      });
      return;
    }

    if (this.formFinished() && this.hasOpenTimeEntry()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não é possível concluir a tarefa enquanto houver apontamento em aberto.',
        life: 4000,
      });
      return;
    }

    this.saving.set(true);

    const payload: Partial<TicketTask> & {
      ticket_status_id?: string | null;
      user_id?: string | null;
      change_ticket_expected_date?: boolean;
    } = {
      summary,
      description,
      estimated_effort: this.formEstimatedEffort(),
      expected_date: this.formExpectedDate()
        ? this.toLocalISOString(this.formExpectedDate() as Date)
        : null,
      difficulty: this.formDifficulty(),
      finished: this.formFinished(),
      ticket_status_id: statusId,
      user_id: userId,
      change_ticket_expected_date: this.formChangeExpectedDate(),
    };

    const editing = this.editingTask();
    if (editing) {
      this.ticketService
        .updateTask(
          this.ticket.id,
          editing.id,
          payload,
          this.dataService.shouldRecalculateTaskEffort
        )
        .subscribe({
          next: updatedTask => {
            this.tasks.update(currentTasks => {
              const index = currentTasks.findIndex(t => t.id === updatedTask.id);
              if (index > -1) {
                const newTasks = [...currentTasks];
                newTasks[index] = {
                  ...newTasks[index],
                  ...updatedTask,
                  summary: updatedTask.summary || '',
                };
                return newTasks;
              }
              return currentTasks;
            });
            this.saving.set(false);
            this.closeForm();
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Tarefa atualizada',
            });
            this.tasksUpdated.emit(this.tasks());
            this.ticketRefreshRequired.emit('status');
          },
          error: () => {
            this.saving.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Erro ao atualizar tarefa',
              life: 5000,
            });
          },
        });
    } else {
      payload.sequence = (this.tasks()?.length ?? 0) + 1;
      this.ticketService
        .createTask(this.ticket.id, payload, this.dataService.shouldRecalculateTaskEffort)
        .subscribe({
          next: newTask => {
            this.tasks.update(currentTasks => [...currentTasks, { ...newTask, progress: 0 }]);
            this.saving.set(false);
            this.closeForm();
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Tarefa criada',
            });
            this.tasksUpdated.emit(this.tasks());
            this.ticketRefreshRequired.emit('status');
          },
          error: () => {
            this.saving.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Erro ao criar tarefa',
              life: 5000,
            });
          },
        });
    }
  }

  confirmDeleteTask(task: TicketTask): void {
    if (!this.canEdit) return;
    this.confirmationService.confirm({
      message: `Deseja realmente excluir a tarefa "${task.summary || task.description}"?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteTask(task),
    });
  }

  private deleteTask(task: TicketTask): void {
    this.ticketService
      .deleteTask(this.ticket.id, task.id, this.dataService.shouldRecalculateTaskEffort)
      .subscribe({
        next: () => {
          this.tasks.update(currentTasks => currentTasks.filter(t => t.id !== task.id));
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Tarefa excluída',
          });
          this.tasksUpdated.emit(this.tasks());
          this.ticketRefreshRequired.emit('status');
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao excluir tarefa',
            life: 5000,
          });
        },
      });
  }

  toggleTaskFinished(task: TaskWithProgress, checked?: boolean): void {
    if (!this.canEdit) return;

    const newFinished = typeof checked === 'boolean' ? checked : !task.finished;
    if (newFinished && this.getActiveTaskLog(task)) {
      this.tasks.update(currentTasks =>
        currentTasks.map(t => (t.id === task.id ? { ...t, finished: false } : t))
      );
      this.cdr.markForCheck();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não é possível concluir a tarefa enquanto houver apontamento em aberto.',
        life: 4000,
      });
      return;
    }

    this.ticketService.updateTask(this.ticket.id, task.id, { finished: newFinished }).subscribe({
      next: updatedTask => {
        this.tasks.update(currentTasks => {
          const index = currentTasks.findIndex(t => t.id === updatedTask.id);
          if (index > -1) {
            const newTasks = [...currentTasks];
            newTasks[index] = {
              ...newTasks[index],
              ...updatedTask,
              summary: updatedTask.summary || '',
            };
            return newTasks;
          }
          return currentTasks;
        });
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Status da tarefa atualizado.',
          life: 3000,
        });
        this.tasksUpdated.emit(this.tasks());
        this.ticketRefreshRequired.emit('status');
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar tarefa',
          life: 5000,
        });
      },
    });
  }

  onStartTimeEntry(task: TicketTask): void {
    if (this.isTimeEntryBlocked(task)) return;
    this.startTimeEntry.emit(task.id);
  }

  onDragStart(index: number): void {
    if (!this.canEdit) return;
    this.draggedIndex.set(index);
  }

  onDragEnd(): void {
    this.draggedIndex.set(null);
    this.dropTarget.set(null);
  }

  onDragLeave(): void {
    this.dropTarget.set(null);
  }

  onDragOver(event: DragEvent, index: number): void {
    if (!this.canEdit) return;
    event.preventDefault();
    const targetElement = event.currentTarget as HTMLElement;
    const rect = targetElement.getBoundingClientRect();
    const isTopHalf = event.clientY < rect.top + rect.height / 2;
    this.dropTarget.set({ index, position: isTopHalf ? 'top' : 'bottom' });
  }

  onDrop(): void {
    if (!this.canEdit) return;
    const sourceIndex = this.draggedIndex();
    const dropTargetInfo = this.dropTarget();

    if (sourceIndex === null || dropTargetInfo === null) {
      this.draggedIndex.set(null);
      this.dropTarget.set(null);
      return;
    }

    let finalTargetIndex = dropTargetInfo.index;
    if (sourceIndex < dropTargetInfo.index && dropTargetInfo.position === 'top') {
      finalTargetIndex--;
    } else if (sourceIndex > dropTargetInfo.index && dropTargetInfo.position === 'bottom') {
      finalTargetIndex++;
    }

    if (sourceIndex === finalTargetIndex) {
      this.draggedIndex.set(null);
      this.dropTarget.set(null);
      return;
    }

    const tasks = [...this.tasks()];
    const [moved] = tasks.splice(sourceIndex, 1);
    tasks.splice(finalTargetIndex, 0, moved);

    // Update sequence property for all tasks
    const updatedTasksWithSequence = tasks.map((task, index) => ({ ...task, sequence: index + 1 }));

    this.tasks.set(updatedTasksWithSequence);
    this.draggedIndex.set(null);

    const payload = updatedTasksWithSequence.map(t => ({ id: t.id, sequence: t.sequence }));
    this.ticketService.reorderTasks(this.ticket.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Ordem das tarefas salva.',
        });
        this.tasksUpdated.emit(this.tasks());
      },
      error: () => {
        this.loadTasksWithProgress();
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao reordenar tarefas',
          life: 5000,
        });
      },
    });
  }

  onHide(): void {
    this.visibleChange.emit(false);
    this.closeForm();
  }

  isTaskToggleDisabled(task: TicketTask): boolean {
    return !this.canEdit || (!!this.getActiveTaskLog(task) && !task.finished);
  }

  getDifficultyClass(difficulty: string): string {
    switch (difficulty?.toUpperCase()) {
      case 'F':
        return 'difficulty-easy';
      case 'M':
        return 'difficulty-medium';
      case 'D':
        return 'difficulty-hard';
      default:
        return 'difficulty-medium';
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  }

  getProgressColor(percentage: number): string {
    if (percentage >= 100) return '#10b981';
    if (percentage >= 50) return '#3b82f6';
    if (percentage > 0) return '#f59e0b';
    return '#94a3b8';
  }

  getTaskTimeEntryIcon(task: TicketTask): string {
    return this.getActiveTaskLog(task) ? 'pi-stop' : 'pi-play';
  }

  getTaskTimeEntryTooltip(task: TicketTask): string {
    if (this.isTimeEntryBlocked(task)) return 'Encerre o apontamento anterior para iniciar.';
    return this.getActiveTaskLog(task) ? 'Encerrar Apontamento' : 'Iniciar Apontamento';
  }

  /**
   * O usuário já tem apontamento ativo neste chamado em outra tarefa — só é permitido um por
   * chamado, então o play das demais tarefas fica bloqueado.
   */
  isTimeEntryBlocked(task: TicketTask): boolean {
    return isTaskTimeEntryBlocked(this.ticket?.ticket_logs, task, getCurrentUserId());
  }

  hasOpenTimeEntry(): boolean {
    const editing = this.editingTask();
    return !!editing && !!this.getActiveTaskLog(editing);
  }

  isFinishedDisabled(): boolean {
    return this.hasOpenTimeEntry() && !this.formFinished();
  }

  getActiveTaskLog(task: TicketTask) {
    return findActiveTaskLog(this.ticket?.ticket_logs, task);
  }

  private toLocalISOString(date: Date): string {
    const offset = -date.getTimezoneOffset();
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetHours = Math.floor(Math.abs(offset) / 60)
      .toString()
      .padStart(2, '0');
    const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');

    return (
      date.getFullYear() +
      '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      '-' +
      date.getDate().toString().padStart(2, '0') +
      'T' +
      date.getHours().toString().padStart(2, '0') +
      ':' +
      date.getMinutes().toString().padStart(2, '0') +
      ':' +
      date.getSeconds().toString().padStart(2, '0') +
      offsetSign +
      offsetHours +
      ':' +
      offsetMinutes
    );
  }
}
